"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.accountBridge = void 0;
const jsHelpers_1 = require("../../../bridge/jsHelpers");
const jsHelpers_2 = require("@ledgerhq/coin-framework/bridge/jsHelpers");
const account_1 = require("./bridgeHelpers/account");
const bignumber_js_1 = __importDefault(require("bignumber.js"));
const fee_1 = require("./bridgeHelpers/fee");
const addresses_1 = require("./bridgeHelpers/addresses");
const errors_1 = require("@ledgerhq/errors");
const rxjs_1 = require("rxjs");
const deviceAccess_1 = require("../../../hw/deviceAccess");
const operation_1 = require("../../../operation");
const icpRosetta_1 = require("./bridgeHelpers/icpRosetta");
const utils_1 = require("../utils");
const errors_2 = require("../errors");
const sync = (0, jsHelpers_1.makeSync)({ getAccountShape: account_1.getAccountShape });
const createTransaction = () => {
    // log("debug", "[createTransaction] creating base tx");
    return {
        family: "internet_computer",
        amount: new bignumber_js_1.default(0),
        fees: (0, fee_1.getEstimatedFees)(),
        recipient: "",
        useAllAmount: false,
    };
};
const prepareTransaction = (a, t) => __awaiter(void 0, void 0, void 0, function* () {
    // log("debug", "[prepareTransaction] start fn");
    const { address } = (0, addresses_1.getAddress)(a);
    const { recipient } = t;
    let amount = t.amount;
    if (recipient && address) {
        // log("debug", "[prepareTransaction] fetching estimated fees");
        if ((yield (0, addresses_1.validateAddress)(recipient)).isValid && (yield (0, addresses_1.validateAddress)(address)).isValid) {
            if (t.useAllAmount) {
                amount = a.spendableBalance.minus(t.fees);
                return Object.assign(Object.assign({}, t), { amount });
            }
        }
    }
    // log("debug", "[prepareTransaction] finish fn");
    return t;
});
const getTransactionStatus = (a, t) => __awaiter(void 0, void 0, void 0, function* () {
    const errors = {};
    const warnings = {};
    const { balance } = a;
    const { address } = (0, addresses_1.getAddress)(a);
    const { recipient, useAllAmount } = t;
    let { amount } = t;
    if (!recipient) {
        errors.recipient = new errors_1.RecipientRequired();
    }
    else if (!(yield (0, addresses_1.validateAddress)(recipient)).isValid) {
        errors.recipient = new errors_1.InvalidAddress();
    }
    else if (recipient.toLowerCase() === address.toLowerCase()) {
        errors.recipient = new errors_1.InvalidAddressBecauseDestinationIsAlsoSource();
    }
    if (!(yield (0, addresses_1.validateAddress)(address)).isValid) {
        errors.sender = new errors_1.InvalidAddress();
    }
    if (!(0, addresses_1.validateMemo)(t.memo).isValid) {
        errors.transaction = new errors_2.InvalidMemoICP();
    }
    // This is the worst case scenario (the tx won't cost more than this value)
    const estimatedFees = t.fees;
    let totalSpent;
    if (useAllAmount) {
        totalSpent = a.spendableBalance;
        amount = totalSpent.minus(estimatedFees);
        if (amount.lte(0) || totalSpent.gt(balance)) {
            errors.amount = new errors_1.NotEnoughBalance();
        }
    }
    else {
        totalSpent = amount.plus(estimatedFees);
        if (amount.eq(0)) {
            errors.amount = new errors_1.AmountRequired();
        }
        else if (totalSpent.gt(a.spendableBalance)) {
            errors.amount = new errors_1.NotEnoughBalance();
        }
    }
    // log("debug", "[getTransactionStatus] finish fn");
    return {
        errors,
        warnings,
        estimatedFees,
        amount,
        totalSpent,
    };
});
const estimateMaxSpendable = ({ account, transaction, }) => __awaiter(void 0, void 0, void 0, function* () {
    var _a;
    const balance = account.balance;
    let maxSpendable = balance.minus((_a = transaction === null || transaction === void 0 ? void 0 : transaction.fees) !== null && _a !== void 0 ? _a : (0, fee_1.getEstimatedFees)());
    if (maxSpendable.isLessThan(0)) {
        maxSpendable = new bignumber_js_1.default(0);
    }
    return maxSpendable;
});
const signOperation = ({ account, deviceId, transaction }) => (0, deviceAccess_1.withDevice)(deviceId)(transport => new rxjs_1.Observable(o => {
    function main() {
        return __awaiter(this, void 0, void 0, function* () {
            // log("debug", "[signOperation] start fn");
            const { recipient, amount } = transaction;
            const { id: accountId, xpub } = account;
            const { address, derivationPath } = (0, addresses_1.getAddress)(account);
            const fee = transaction.fees;
            const { unsignedTxn, payloads } = yield (0, icpRosetta_1.getUnsignedTransaction)(transaction, account);
            o.next({
                type: "device-signature-requested",
            });
            const { signedTxn } = yield (0, icpRosetta_1.signICPTransaction)({
                unsignedTxn,
                transport,
                path: (0, utils_1.getPath)(derivationPath),
                payloads,
                pubkey: xpub !== null && xpub !== void 0 ? xpub : "",
            });
            const { hash } = yield (0, icpRosetta_1.getTxnMetadata)(signedTxn);
            o.next({
                type: "device-signature-granted",
            });
            const operation = {
                id: (0, operation_1.encodeOperationId)(accountId, hash, "OUT"),
                hash,
                type: "OUT",
                senders: [address],
                recipients: [recipient],
                accountId,
                value: amount.plus(fee),
                fee,
                blockHash: null,
                blockHeight: null,
                date: new Date(),
                extra: {
                    memo: transaction.memo,
                },
            };
            o.next({
                type: "signed",
                signedOperation: {
                    operation,
                    signature: signedTxn,
                    expirationDate: (0, icpRosetta_1.getTxnExpirationDate)(unsignedTxn),
                },
            });
        });
    }
    main().then(() => o.complete(), e => o.error(e));
}));
const receive = (0, jsHelpers_1.makeAccountBridgeReceive)();
const broadcast = ({ signedOperation: { signature, operation } }) => __awaiter(void 0, void 0, void 0, function* () {
    // log("debug", "[broadcast] start fn");
    yield (0, icpRosetta_1.broadcastTxn)(signature);
    const result = Object.assign({}, operation);
    return result;
});
exports.accountBridge = {
    sync,
    updateTransaction: jsHelpers_2.defaultUpdateTransaction,
    createTransaction,
    prepareTransaction,
    getTransactionStatus,
    estimateMaxSpendable,
    receive,
    signOperation,
    broadcast,
};
//# sourceMappingURL=account.js.map